import {
  Button,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  AlertBadgeRegular,
  ArrowSwapRegular,
  ChevronDownRegular,
  DocumentBulletListRegular,
  PlugConnectedRegular,
  PuzzlePieceRegular,
  ServerRegular,
  SettingsRegular,
} from "@fluentui/react-icons";
import React, { useState } from "react";
import { useNavigate } from "react-router";

import { checkIsMfaSettingRequired } from "@/helpers/checkIsMfaSettingRequired";
import optiumLogo from "@/images/optium_logo.svg";

import { useAppRouteParams } from "@/AppRoutes";
import { translation } from "@/i18n";
import { nsOrg } from "@/i18n/locales/org";
import { nsSideNav } from "@/i18n/locales/sideNav";
import { useAppAccount } from "../AppProvider/AppAccountProvider";
import { useAppOrgsAndInvitedOrgs } from "../AppProvider/AppOrgsAndInvitedOrgsProvider";
import { OrganizationSwitchDialog } from "../AppSideNav/AppSideNavGlobal/OrganizationSwitchDialog";
import { useCachedOrganization } from "../InOrganizationProvider/OrganizationProvider";
import { usePermissions } from "../InOrganizationProvider/PermissionsProvider";
import { OrganizationAvatar } from "../OrganizationAvatar";

const ICON_COLOR = "var(--figma-color-lightBlack)";
const SUBMENU_HOVER_DELAY_MS = 100;
const WARNING_COLOR = "var(--figma-color-status-warning)";

const useTranslation = translation.use(nsOrg, nsSideNav);

const useStyles = makeStyles({
  popover: {
    minWidth: "260px",
    maxWidth: "300px",
    maxHeight: "calc(100dvh - 57px)",
    overflowY: "auto",
    paddingBottom: "8px",
  },
  menuItem: {
    paddingLeft: "8px",
    gap: "8px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("6px"),
    ...shorthands.padding("7px"),
  },
  headerName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    wordBreak: "break-word",
  },
  headerLogo: {
    height: "22px",
  },
  logoTrigger: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("4px"),
    ...shorthands.padding("4px", "8px"),
    ...shorthands.border("none"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: "transparent",
    cursor: "pointer",
    color: tokens.colorNeutralForeground2,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  logoTriggerImg: {
    height: "22px",
  },
  staticLogo: {
    display: "flex",
    alignItems: "center",
    ...shorthands.padding("4px", "8px"),
  },
  triggerOrgName: {
    maxWidth: "220px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  switchButtonWrapper: {
    ...shorthands.padding("0", "6px", "10px"),
  },
  switchButton: {
    width: "100%",
  },
  invited: {
    paddingLeft: "8px",
    gap: "8px",
    // Fluent MenuItem の hover/focus が文字色を上書きするため、warning 色を維持する
    color: "var(--figma-color-status-warning)",
    ":hover": {
      color: "var(--figma-color-status-warning)",
    },
    ":hover:active": {
      color: "var(--figma-color-status-warning)",
    },
    ":focus": {
      color: "var(--figma-color-status-warning)",
    },
  },
});

/** 組織設定サブメニュー。表示権限は組織設定ページのタブと整合させること。 */
type OrgSettingSubItem = {
  id: string;
  label: string;
  visible: boolean;
};

export const useHasOrganizationMenuContent = (): boolean => {
  const { account } = useAppAccount();
  const { organizationId } = useAppRouteParams();
  const { organizations: appOrganizations, invitedOrganizations } =
    useAppOrgsAndInvitedOrgs();
  const { organization: memberOrganization } = useCachedOrganization();
  const organization =
    memberOrganization ||
    appOrganizations.find((org) => org.org_id === organizationId);
  const isMfaSettingRequired = checkIsMfaSettingRequired(account);
  return (
    !!organization ||
    appOrganizations.length > 1 ||
    (!isMfaSettingRequired && invitedOrganizations.length > 0)
  );
};

/**
 * サイドバー上部の組織ドロップダウン (組織切替・組織設定・アドオン・Connect)。
 * children: トリガー要素 (省略時は OPTIUM ロゴ or 組織アイコン + 組織名 + ChevronDown)。
 * MenuTrigger が ref/onClick を注入するため単一要素にすること。
 */
export const OrganizationMenu: React.FC<{
  children?: React.ReactElement;
}> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const styles = useStyles();

  const { account, isAADeveloperOrAbove } = useAppAccount();
  const { organizationId } = useAppRouteParams();
  const { organizations: appOrganizations, invitedOrganizations } =
    useAppOrgsAndInvitedOrgs();
  const { organization: memberOrganization } = useCachedOrganization();
  const { hasPermission } = usePermissions();

  const appOrganization = appOrganizations.find(
    (org) => org.org_id === organizationId
  );
  const organization = memberOrganization || appOrganization;
  const organizationName = organization?.name ?? "";

  const isMfaSettingRequired = checkIsMfaSettingRequired(account);
  const canSwitchOrganization = appOrganizations.length > 1;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSwitchDialogVisible, setIsSwitchDialogVisible] = useState(false);

  const hasMenuContent = useHasOrganizationMenuContent();
  if (!children && !hasMenuContent) {
    return (
      <div className={styles.staticLogo}>
        <img src={optiumLogo} alt="OPTIUM" className={styles.logoTriggerImg} />
      </div>
    );
  }

  const closeMenu = () => setIsMenuOpen(false);

  const orgSettingSubItems: OrgSettingSubItem[] = [
    {
      id: "organization_info",
      label: t.org.menu.settingItem.organizationInfo(),
      visible: !!organization,
    },
    {
      id: "functions",
      label: t.org.menu.settingItem.functions(),
      visible: !!organization,
    },
    {
      id: "members",
      label: t.org.menu.settingItem.members(),
      visible: hasPermission("members", "read"),
    },
    {
      id: "version_categories",
      label: t.org.menu.settingItem.categories(),
      visible: hasPermission("version_categories", "read"),
    },
    {
      id: "roles",
      label: t.org.menu.settingItem.roles(),
      visible: hasPermission("roles", "read"),
    },
    {
      id: "notification",
      label: t.org.menu.settingItem.notifications(),
      visible: hasPermission("organizations_notification_channel", "read"),
    },
    {
      id: "cli_api_keys",
      label: t.org.menu.settingItem.cliApiKeys(),
      visible: isAADeveloperOrAbove,
    },
  ];

  // 招待中/notFound 等で organization が未解決なら (AA 権限でも) 存在しない組織へのリンクを出さない
  const showAlgorithmVersions =
    !!organization && hasPermission("algorithm_versions", "read");
  const showFrontendAddons =
    !!organization &&
    (isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "read"));
  const showConnectMasters =
    isAADeveloperOrAbove || hasPermission("connect_masters_owned", "read");
  const showConnect = hasPermission("connects", "read");
  // Endpoint Addon系は管理者 API のみで扱われるため aa:developer 以上に限定する。
  const showEndpointAddonMasters = isAADeveloperOrAbove;
  const showEndpointAddons = isAADeveloperOrAbove;
  // Connect とEndpoint Addonはアドオングループ内で並列のサブメニューとして表示する。
  const showConnectAddon =
    !!organization && (showConnectMasters || showConnect);
  const showEndpointAddonAddon =
    !!organization && (showEndpointAddonMasters || showEndpointAddons);
  const showAddonSection =
    showAlgorithmVersions ||
    showFrontendAddons ||
    showConnectAddon ||
    showEndpointAddonAddon;

  return (
    <>
      <Menu
        open={isMenuOpen}
        onOpenChange={(_e, data) => setIsMenuOpen(data.open)}
        positioning={{
          position: "below",
          align: "start",
          offset: { mainAxis: 5, crossAxis: 5 },
        }}
      >
        <MenuTrigger disableButtonEnhancement>
          {children ?? (
            <button
              type="button"
              className={styles.logoTrigger}
              aria-label={t.org.menu.ariaLabel()}
            >
              {organization ? (
                <>
                  <OrganizationAvatar name={organizationName} size={24} />
                  <span className={styles.triggerOrgName}>
                    {organizationName}
                  </span>
                </>
              ) : (
                <img
                  src={optiumLogo}
                  alt="OPTIUM"
                  className={styles.logoTriggerImg}
                />
              )}
              <ChevronDownRegular />
            </button>
          )}
        </MenuTrigger>
        <MenuPopover className={styles.popover}>
          <div className={styles.header}>
            {organization ? (
              <>
                <OrganizationAvatar name={organizationName} size={32} />
                <div className={styles.headerName}>{organizationName}</div>
              </>
            ) : (
              <img
                src={optiumLogo}
                alt="OPTIUM"
                className={styles.headerLogo}
              />
            )}
          </div>

          {canSwitchOrganization && (
            <div className={styles.switchButtonWrapper}>
              <Button
                className={styles.switchButton}
                icon={<ArrowSwapRegular />}
                onClick={() => {
                  closeMenu();
                  setIsSwitchDialogVisible(true);
                }}
              >
                {t.org.menu.switchButton()}
              </Button>
            </div>
          )}

          <MenuList>
            {!isMfaSettingRequired && invitedOrganizations.length > 0 && (
              <MenuItem
                className={styles.invited}
                icon={<AlertBadgeRegular color={WARNING_COLOR} />}
                onClick={() => navigate("/organizations?tab=invited")}
              >
                {t.org.menu.pendingInvitations({
                  count: invitedOrganizations.length,
                })}
              </MenuItem>
            )}

            {organization && (
              <>
                <MenuDivider />
                <MenuGroup>
                  <MenuGroupHeader>
                    {t.org.menu.organizationGroup()}
                  </MenuGroupHeader>
                  <Menu hoverDelay={SUBMENU_HOVER_DELAY_MS}>
                    <MenuTrigger disableButtonEnhancement>
                      <MenuItem
                        className={styles.menuItem}
                        icon={<SettingsRegular color={ICON_COLOR} />}
                        onClick={() => {
                          closeMenu();
                          navigate(
                            `/organizations/${organizationId}/organization_setting/organization_info`
                          );
                        }}
                      >
                        {t.org.menu.setting()}
                      </MenuItem>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        {orgSettingSubItems
                          .filter((item) => item.visible)
                          .map((item) => (
                            <MenuItem
                              className={styles.menuItem}
                              key={item.id}
                              onClick={() =>
                                navigate(
                                  `/organizations/${organizationId}/organization_setting/${item.id}`
                                )
                              }
                            >
                              {item.label}
                            </MenuItem>
                          ))}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </MenuGroup>
              </>
            )}

            {showAddonSection && (
              <>
                <MenuDivider />
                <MenuGroup>
                  <MenuGroupHeader>{t.org.menu.addonGroup()}</MenuGroupHeader>
                  {showAlgorithmVersions && (
                    <MenuItem
                      className={styles.menuItem}
                      icon={<DocumentBulletListRegular color={ICON_COLOR} />}
                      onClick={() =>
                        navigate(
                          `/organizations/${organizationId}/algorithm_versions`
                        )
                      }
                    >
                      {t.sideNav.item.algorithmVersions()}
                    </MenuItem>
                  )}
                  {showFrontendAddons && (
                    <MenuItem
                      className={styles.menuItem}
                      icon={<PuzzlePieceRegular color={ICON_COLOR} />}
                      onClick={() =>
                        navigate(
                          `/organizations/${organizationId}/frontend_addons`
                        )
                      }
                    >
                      {t.sideNav.item.frontendAddons()}
                    </MenuItem>
                  )}
                  {showConnectAddon && (
                    <Menu hoverDelay={SUBMENU_HOVER_DELAY_MS}>
                      <MenuTrigger disableButtonEnhancement>
                        <MenuItem
                          className={styles.menuItem}
                          icon={<PlugConnectedRegular color={ICON_COLOR} />}
                        >
                          Connect
                        </MenuItem>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          {showConnectMasters && (
                            <MenuItem
                              className={styles.menuItem}
                              onClick={() =>
                                navigate(
                                  `/organizations/${organizationId}/connect_masters`
                                )
                              }
                            >
                              {t.sideNav.item.connectMasters()}
                            </MenuItem>
                          )}
                          {showConnect && (
                            <MenuItem
                              className={styles.menuItem}
                              onClick={() =>
                                navigate(
                                  `/organizations/${organizationId}/connects`
                                )
                              }
                            >
                              Connect
                            </MenuItem>
                          )}
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  )}
                  {showEndpointAddonAddon && (
                    <Menu hoverDelay={SUBMENU_HOVER_DELAY_MS}>
                      <MenuTrigger disableButtonEnhancement>
                        <MenuItem
                          className={styles.menuItem}
                          icon={<ServerRegular color={ICON_COLOR} />}
                        >
                          Endpoint Addon
                        </MenuItem>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          {showEndpointAddonMasters && (
                            <MenuItem
                              className={styles.menuItem}
                              onClick={() =>
                                navigate(
                                  `/organizations/${organizationId}/endpoint_addon_masters`
                                )
                              }
                            >
                              {t.sideNav.item.endpointAddonMasters()}
                            </MenuItem>
                          )}
                          {showEndpointAddons && (
                            <MenuItem
                              className={styles.menuItem}
                              onClick={() =>
                                navigate(
                                  `/organizations/${organizationId}/endpoint_addons`
                                )
                              }
                            >
                              Endpoint Addon
                            </MenuItem>
                          )}
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  )}
                </MenuGroup>
              </>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>

      <OrganizationSwitchDialog
        isVisibleDialog={isSwitchDialogVisible}
        closeDialog={() => setIsSwitchDialogVisible(false)}
      />
    </>
  );
};
